import React from 'react';
import compact from 'lodash.compact';
import { COLORS, NA, TITLE, LICENSE } from '../../../constants/shared/CommonConstants';
import { ASTERISK } from '../../../constants/shared/CellConstants';
import { PERFORMANCE_LEVEL, LABEL, TRC_SCORE } from '../../../constants/shared/TrcConstants';
import { getPerformanceLevelAbbr, getPerformanceLevelTitle } from '../../../utils/shared/trcUtils';

const { EXPRESS } = LICENSE;
const { BENCHMARK_TITLE } = TITLE;
const { YELLOW, GREEN, BLUE, RED } = COLORS;
const { FRUSTRATIONAL, INSTRUCTIONAL, INDEPENDENT, PROFICIENT, NOT_PROFICIENT } = PERFORMANCE_LEVEL;
const { NON_FICTION, FICTION, F_LABEL, N_LABEL } = LABEL;

export const STATIC_ICONS = [
    {
        key: NA,
        iconText: NA,
        firstTitle: 'Not Available',
    },
];

export const TK = 'TK';

export const PROGRESS_ICONS_GROUP = [
    {
        name: 'Progress Monitoring Graph',
        icons: compact([
            {
                key: 'BM score',
                iconClassName: 'bm-data-point-icon',
                firstTitle: 'Benchmark',
                secondTitle: 'Score',
            },
            {
                key: 'PM score',
                iconClassName: 'pm-data-point-icon',
                firstTitle: 'Progress Monitoring',
                secondTitle: 'Score',
            },
            {
                key: 'Benchmark goal',
                iconClassName: 'default-goal-icon',
                firstTitle: 'Benchmark',
                secondTitle: 'Goal',
            },
            {
                key: 'Customized goal',
                iconClassName: 'custom-goal-icon',
                firstTitle: 'Custom Goal',
                secondTitle: 'Set',
            },
            {
                key: 'Aim line',
                iconClassName: 'aim-line-icon',
                firstTitle: 'Aim Line',
            },
            {
                key: 'Trend line',
                iconClassName: 'trend-line-icon',
                firstTitle: 'Trend Line',
            },
            {
                key: 'Off Grade',
                iconClassName: 'off-grade-icon',
                firstTitle: 'Off Grade Result',
            },
        ]),
    },
];

export const TRC_ICONS = [
    {
        iconText: ASTERISK,
        firstTitle: (
            <>
                Displaying <br />
                Official Results
            </>
        ),
        secondTitle: 'Only',
        isAvailable: ({ licenses, trc }) => licenses?.includes(trc),
    },
    {
        iconClassName: 'icon-amplify-pause',
        firstTitle: 'Paused',
        secondTitle: 'Assessment',
        isAvailable: ({ licenses, trc }) => licenses?.includes(trc) || licenses?.includes(EXPRESS),
    },
];

export const LICENSE_DEPENDENT = [
    {
        iconClassName: 'icon-tk',
        firstTitle: 'Transitional',
        secondTitle: 'Kindergarten',
        isAvailable: ({ licenses }) => licenses?.includes(TK),
    },
    ...TRC_ICONS,
    {
        iconClassName: 'icon-amplify-info-outline',
        firstTitle: 'Risk Indicator',
        isAvailable: ({ hasRiskIndicator }) => hasRiskIndicator,
    },
    {
        iconText: <b>E</b>,
        iconClassName: 'icon-e-composite square',
        firstTitle: (
            <>
                Equated <br />
                Composite
            </>
        ),
        secondTitle: 'Score',
        isAvailable: ({ isSecure }) => isSecure,
    },
];

export const PROBE_DETAILS_ICONS_GROUP = {
    name: 'Probe Details',
    icons: [
        {
            iconClassName: 'green-box',
            iconText: (
                <>
                    85
                    <br /> <div className="legend-interp">{BENCHMARK_TITLE}</div>
                </>
            ),
        },
        {
            iconClassName: 'wrap',
            iconText: (
                <>
                    Click on a score to
                    <br /> view a student’s
                    <br /> assessment details.
                </>
            ),
        },
    ],
};

export const TRC_DESCRIPTION_MAP = [
    {
        firstAbbr: getPerformanceLevelAbbr(INDEPENDENT),
        firstTitle: getPerformanceLevelTitle(INDEPENDENT),
        secondAbbr: F_LABEL,
        secondTitle: `${FICTION} Book`,
    },
    {
        firstAbbr: getPerformanceLevelAbbr(INSTRUCTIONAL),
        firstTitle: getPerformanceLevelTitle(INSTRUCTIONAL),
        secondAbbr: N_LABEL,
        secondTitle: `${NON_FICTION} Book`,
    },
    {
        firstAbbr: getPerformanceLevelAbbr(FRUSTRATIONAL),
        firstTitle: getPerformanceLevelTitle(FRUSTRATIONAL),
        secondAbbr: ASTERISK,
        secondTitle: `Official Result`,
    },
    {
        firstAbbr: TRC_SCORE.PS,
        firstTitle: (
            <>
                Presumed
                <br /> Score
            </>
        ),
    },
];

export const PERFORMANCE_LEVEL_MAP = [
    { color: RED, levelTitle: 'Well Below' },
    { color: YELLOW, levelTitle: 'Below' },
    {
        color: GREEN,
        levelTitle: (
            <>
                Benchmark/
                <br /> Proficient
            </>
        ),
    },
    { color: BLUE, levelTitle: 'Above' },
];

const TRC_ACCURACY_ICONS = [
    {
        iconClassName: 'icon-frustrational',
        firstTitle: 'Frustrational',
        secondTitle: '(89% or below)',
    },
    {
        iconClassName: 'icon-instructional',
        firstTitle: 'Instructional',
        secondTitle: '(90-94%)',
    },
    {
        iconClassName: 'icon-independent',
        firstTitle: 'Independent',
        secondTitle: '(95% or higher)',
    },
];

const TRC_READ_WRIT_COMP_ICONS = [
    {
        iconClassName: 'square icon-red',
        iconText: <strong>{getPerformanceLevelAbbr(NOT_PROFICIENT)}</strong>,
        firstTitle: getPerformanceLevelTitle(NOT_PROFICIENT),
    },
    {
        iconText: <strong>{getPerformanceLevelAbbr(PROFICIENT)}</strong>,
        firstTitle: getPerformanceLevelTitle(PROFICIENT),
    },
];

export const TRC_SUMMARY_ICONS_GROUP = [
    { name: 'Accuracy', icons: TRC_ACCURACY_ICONS },
    {
        name: (
            <>
                Reading Comp/
                <br />
                Written Comp
            </>
        ),
        icons: TRC_READ_WRIT_COMP_ICONS,
    },
];
